import {
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  Radio,
  RadioGroup,
} from "@mui/material";
import React from "react";
import { Field, FieldRenderProps, useFormState } from "react-final-form";
import { deepIndex } from "../../deepIndex";
import { FormFieldWrapper, FormFieldWrapperProps } from "./FormFieldWrapper";

export interface RadioFormFieldOption {
  value: string | number;
  text?: string | number;
}

//Courtesy of https://stackoverflow.com/questions/58099124/how-to-use-custom-radio-component-with-react-final-form
const RadioWrapper = (
  props: FieldRenderProps<string | number, HTMLElement, string | number> & {
    disabled?: boolean;
    errorId?: string;
  },
) => {
  const {
    disabled,
    errorId,
    input: { checked, value, name, onChange, ...restInput },
  } = props;

  return (
    <Radio
      name={name}
      inputProps={{
        ...restInput,
        "aria-describedby": errorId,
        "aria-invalid": Boolean(errorId),
      }}
      onChange={onChange}
      checked={checked}
      value={value}
      disabled={disabled}
    />
  );
};

export interface RadioFormFieldProps
  extends Omit<FormFieldWrapperProps, "children"> {
  name: string;
  label?: string;
  options: RadioFormFieldOption[] | string[];
  required?: boolean;
  disabled?: boolean;
}

export const RadioFormField: React.FC<RadioFormFieldProps> = ({
  name,
  label,
  options,
  required,
  disabled,
  ...wrapperProps
}) => {
  const { errors, visited, submitFailed } = useFormState({
    subscription: { errors: true, visited: true, submitFailed: true },
  });
  const targetError = deepIndex(errors, name);
  const targetVisited = deepIndex(visited, name);
  const showError = Boolean(
    (targetVisited || submitFailed || disabled) && targetError,
  );
  const fieldId = React.useId();
  const labelId = `${fieldId}-label`;
  const errorId = showError ? `${fieldId}-error` : undefined;

  return (
    <FormFieldWrapper {...wrapperProps}>
      <FormControl required={required} error={showError}>
        {label && <FormLabel id={labelId}>{label}</FormLabel>}
        <RadioGroup
          aria-labelledby={label ? labelId : undefined}
          aria-describedby={errorId}
          aria-invalid={showError}
          aria-required={required}
          row
          name={name}
        >
          {options.map((o) => (
            <FormControlLabel
              key={typeof o === "string" ? o : o.value}
              label={typeof o === "string" ? o : (o.text ?? o.value)}
              control={
                <Field
                  name={name}
                  value={typeof o === "string" ? o : o.value}
                  type="radio"
                  validate={(v) =>
                    required && v == null ? "This field is required" : undefined
                  }
                  render={({ input, meta }) => (
                    <RadioWrapper
                      input={input}
                      meta={meta}
                      disabled={disabled}
                      errorId={errorId}
                    />
                  )}
                />
              }
            />
          ))}
        </RadioGroup>
        {showError && (
          <FormHelperText id={errorId} error>
            {targetError}
          </FormHelperText>
        )}
      </FormControl>
    </FormFieldWrapper>
  );
};
